import React, { useEffect, useState } from 'react';
import { APICallStatus, CalendarEvent } from '../../Types/UnitFeatureTypes';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Grid2, IconButton, Paper, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material';
import { createEventAPIStatus, updateEventAPIStatus } from '../../Redux/selectors';
import { useApp } from '../../context/appContext';
import { Actions } from '../../Redux/Reducer/rootReducer';
import { Cancel, Save, Send } from '@mui/icons-material';
import Editor from '../TextEditor/Editor';
import DropdownSelection from '../CommonComponents/DropdownSelection';
import { useAuth } from '../../context/authContext';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import DatePickerComponent from '../CommonComponents/CustomDatePicker';


const CreateEvent: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<CalendarEvent>({
      id: -1,
      title: '',
      start_time: new Date(),
      end_time: new Date(),
      description: '',
      location: '',
      all_day: false,
      editable: false,
      capacity: 1000,
      creater: {
        id: user?.id || -1,
        name: user?.first_name + ' ' + user?.last_name || ''
      }
    })

  const [alertInfo, setAlertInfo] = useState<{
    open: boolean;
    message: string;
    severity: "error" | "warning" | "info" | "success";
  }>({
      open: false,
      message: "",
      severity: "info",
  });
    
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { setSnackbar } = useApp();

  const eventCreatedAPI = useSelector(createEventAPIStatus);
  const eventUpdatedAPI = useSelector(updateEventAPIStatus);

  useEffect(() => {
    if (eventCreatedAPI === APICallStatus.SUCCESS) {
      navigate(-1);
      setSnackbar(true, 'Event created successfully', 'success');
      dispatch(Actions.event.resetStatusRequestAction());
    } else if (eventCreatedAPI === APICallStatus.FAILURE) {
      setSnackbar(true, 'Failed to create event', 'error');
      dispatch(Actions.event.resetStatusRequestAction());
    }
  }, [eventCreatedAPI]);


  useEffect(() => {
    if (eventUpdatedAPI === APICallStatus.SUCCESS) {
      navigate(-1);
      setSnackbar(true, 'Event updated successfully', 'success');
      dispatch(Actions.event.resetStatusRequestAction());
    } else if (eventUpdatedAPI === APICallStatus.FAILURE) {
      setSnackbar(true, 'Failed to update event', 'error');
      dispatch(Actions.event.resetStatusRequestAction());
    }
  }, [eventUpdatedAPI]);


  useEffect(() => {
    if (location.state && (location?.state?.edit || location.state.expanded) ) {
      const event = location.state.event as CalendarEvent;
      setFormData({
        id: event.id,
        title: event.title,
        start_time: event.start_time,
        end_time: event.end_time,
        description: event.description,
        location: event.location,
        all_day: event.all_day,
        editable: event.editable,
        creater: event.creater,
        capacity: event.capacity
      });
    }
  }, [location]);
    
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors(errors => ({...errors, [name as string]: ''}));
  };
    

  const validateForm = () => {
      const newErrors: { [key: string]: string } = {};
    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';

    if (!formData.start_time) {
        errors.start = 'Start Date is required';
    }
    if (!formData.end_time) {
        errors.end = 'End Date is required';
    }
    if (formData.start_time < new Date()) {
        errors.start = 'Start Date should be greater than current date';
    }
    if (formData.end_time < formData.start_time) {
        errors.end = 'End Date should be greater than Start Date';
    }
    if (!formData.capacity) {
        errors.capacity = 'Capacity is required';
    }
    setErrors(newErrors);
    const buildMessage: string = Object.values(newErrors)[0];
    if (buildMessage) {
        showAlert(buildMessage, 'error');
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (location.state && location.state.edit) {
      dispatch(Actions.event.editEventRequestAction.request({eventData: formData}));
    } else {
      dispatch(Actions.event.createEventRequestAction.request({eventData: formData}));
    }

    // setFormData({
    //   title: '',
    //   start: new Date(),
    //   end: new Date(),
    //   description: '',
    //   location: '',
    //   all_day: false,
    //   editable: false,
    //   creater: {
    //     id: user?.id || -1,
    //     name: user?.first_name + ' ' + user?.last_name || ''
    //   }
    // });
    setErrors({});
  };

  const showAlert = (message: string, severity: "error" | "warning" | "info" | "success") => {
    setAlertInfo({ open: true, message, severity });
    setTimeout(() => {
        handleCloseAlert();
    }
    , 6000);
}

const handleCloseAlert = () => {
    setAlertInfo((prev) => ({ ...prev, open: false }));
};

  return (
    <Paper elevation={5} sx={{ padding: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h5" gutterBottom>
          {location?.state?.edit ? 'Edit' : 'Create'} Event
        </Typography>
        <IconButton sx={{ '&:hover': { color: 'error.main' } }} onClick={() => navigate(-1)}>
          <Cancel />
        </IconButton>
      </Box>
      {alertInfo.open && (
          <Box sx={{ paddingBottom: '1rem' }}>
              <Alert onClose={handleCloseAlert} severity={alertInfo.severity}>
                  {alertInfo.message}
              </Alert>
          </Box>
      )}
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Title"
          variant="outlined"
          name="title"
          placeholder='Please provide a title for the issue.'
          margin='normal'
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title}
        />

        <Editor
          value={formData.description}
          placeholder='Please write the details of violation here.'
          error={!!errors.description}
          helperText={errors.description}
          onChange={(value: string) => handleChange({ target: { name: 'description', value } } as React.ChangeEvent<HTMLInputElement>)}
        />

        <Stack direction='row' sx={{mt:2}} spacing={1}>
          <DatePickerComponent
            labelValue="Start Date"
            selectedDate={dayjs(formData.start_time)}
            setSelectedDate={(date) => setFormData((prevState) => ({ ...prevState, start_time: date?.toDate() || new Date()}))}
            minDate={dayjs()}
          />
          <DatePickerComponent  
              labelValue="End Date"
              selectedDate={dayjs(formData.end_time)}
              setSelectedDate={(date) => setFormData((prevState) => ({ ...prevState, end_time: date?.toDate() || new Date()}))}
              minDate={dayjs(formData.start_time)}
          />
        </Stack>
        <Stack direction='row' sx={{mt:2}} spacing={1}>
            <TimePicker
              sx={{width: '100%'}}
              label="Start Time"
              value={dayjs(formData.start_time)}
              onChange={(date) => setFormData((prevState) => ({ ...prevState, start_time: date?.toDate() || new Date()}))}
              ampm={false}
              minTime={dayjs(formData.start_time).isSame(dayjs(), 'day') ? dayjs().add(5, 'minute') : undefined}
            />
            <TimePicker
              sx={{width: '100%'}}
              label="End Time"
              value={dayjs(formData.end_time)}
              onChange={(date) => setFormData((prevState) => ({ ...prevState, end_time: date?.toDate() || new Date()}))}
              ampm={false}
              minTime={dayjs(formData.start_time).isSame(dayjs(formData.end_time), 'day') ? dayjs(formData.start_time).add(5, 'minute') : undefined}
            />
        </Stack>
        <Stack direction='row' sx={{mt:2}} spacing={1}>
        <TextField
          fullWidth
          label="Location"
          variant="outlined"
          name="location"
          placeholder='Please provide a location for the event.'
          margin='normal'
          value={formData.location}
          onChange={handleChange}
          error={!!errors.location}
          helperText={errors.location}
        />
        <TextField
          fullWidth
          label="Capacity"
          variant="outlined"
          name="capacity"
          type='number'
          slotProps={{ htmlInput: {min: 0} }}
          placeholder='Please provide a capacity for the event.'
          margin='normal'
          value={formData.capacity}
          onChange={handleChange}
          error={!!errors.capacity}
          helperText={errors.capacity}
        />
        </Stack>
        <Grid2 container spacing={2} sx={{mt: 2}}>
          <Grid2 size={{xs: 12, md: 6}}>
            <Button fullWidth variant="contained" color="secondary" onClick={(e) => navigate(-1)}>
                <Cancel sx={{mr: 2}} /> Cancel
            </Button>
          </Grid2>
          <Grid2 size={{xs: 12, md: 6}}>
            <Button fullWidth type="submit" variant="contained" color="primary">
                {location?.state?.edit ? "Update" : "Create"} Event <Save sx={{ml: 2}} />
            </Button>
          </Grid2>
        </Grid2>
      </form>
    </Paper>
  );
    
}

export default CreateEvent;